import { Box, Button, EmptyState } from '@wix/design-system';
import { errorScreenIconSvg } from '@wix/wix-chatbot-common/icons';
import { Trans, useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useSelector, useStore } from 'react-redux';
import { useLocation, useRouteError } from 'react-router-dom';

import { biLogger } from '../bi/biLogger';
import { pageEnumToString } from '../bi/utils';
import { useCurrentPage } from '../common/hooks/useCurrentPage';
import { selectLastClickedLink } from '../common/selectors/lastClickedLink';
import { formatErrorConstructorName } from '../util/errorFormatting';

type ErrorScreenProps = {
  onRefresh?: () => void;
};

const reload = () => {
  try {
    window.parent.location.reload();
  } catch (e) {
    // probably cross-origin error and we can refresh only the iframe
    window.location.reload();
  }
};

export const ErrorScreen: FC<ErrorScreenProps> = ({ onRefresh }) => {
  const store = useStore();
  const location = useLocation();
  const lastClickedLink = useSelector(selectLastClickedLink);
  const error = useRouteError();
  const currentPage = useCurrentPage();

  useEffect(() => {
    const logError = async () => {
      const state = store.getState();
      const widgetPageType = pageEnumToString(currentPage);
      let errorStatus;
      if (error instanceof Response && error.status) {
        errorStatus = error.status.toString();
      } else if (typeof error === 'number') {
        errorStatus = error.toString();
      } else if (error instanceof Error) {
        errorStatus = formatErrorConstructorName(error.name);
      } else if (typeof error === 'string') {
        const rawError = error === 'ERROR' ? 'unknown error' : error;
        errorStatus = formatErrorConstructorName(rawError);
      } else if (error && typeof error === 'object') {
        const constructorName = error.constructor?.name || 'Object';
        errorStatus = formatErrorConstructorName(constructorName);
      } else {
        errorStatus = formatErrorConstructorName('unknown error');
      }
      const clickedUrl = lastClickedLink?.url;
      const clickedText = lastClickedLink?.text || `${widgetPageType} link`;

      await biLogger.helpWidgetPageErrorNew(state, {
        widget_page_type: widgetPageType,
        error_type: errorStatus,
        clicked_url: clickedUrl,
        clicked_text: clickedText,
      });
    };

    logError();
  }, [error, store, location, lastClickedLink, currentPage]);

  const refresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    } else {
      reload();
    }
  }, [onRefresh]);

  const { t } = useTranslation();
  const subtitle = useMemo(
    () => (
      <Trans
        t={t}
        values={{ link: t('fatal_error_state_status_page') }}
        components={{
          a: (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a target="_blank" rel="noreferrer" href="https://status.wix.com" />
          ),
        }}
      >
        fatal_error_state_subtitle
      </Trans>
    ),
    [t],
  );

  return (
    <Box width="100%" align="center" height="100%" verticalAlign="middle">
      <EmptyState
        theme="page-no-border"
        image={React.createElement(errorScreenIconSvg, {})}
        title={t('fatal_error_state_title')}
        subtitle={subtitle}
      >
        <Button priority="secondary" onClick={refresh}>
          {t('fatal_error_state_refresh')}
        </Button>
      </EmptyState>
    </Box>
  );
};
